/**
 * ServiceDeliveryForm — analyst-authored "what we did this month".
 *
 * Structured categories the analyst fills during report creation (solutions,
 * analytics, hunts, cloud-app discovery). Emitted up to the parent and passed
 * into generate; rendered verbatim into the report — the writing agent never
 * touches it. Empty → emits undefined so the section is omitted.
 */

import { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { MarkdownField } from './MarkdownField';
import type {
  ServiceDelivery,
  ServiceDeliveryCloudApp,
} from '../../services/reportGeneration';

interface Props {
  onChange: (sd: ServiceDelivery | undefined) => void;
}

const splitLines = (s: string): string[] =>
  s.split('\n').map((x) => x.trim()).filter(Boolean);

function ListField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-text-secondary mb-1">
        {label} <span className="font-normal">(one per line)</span>
      </label>
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={hint}
        className="w-full px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-sm"
      />
    </div>
  );
}

const APP_FIELDS: Array<{ key: keyof ServiceDeliveryCloudApp; label: string; placeholder: string }> = [
  { key: 'hosting', label: 'Hosting / domains', placeholder: '*.stores4domains.com | *.secureserver.net' },
  { key: 'dataCenter', label: 'Data center', placeholder: 'United States' },
  { key: 'auth', label: 'Auth', placeholder: 'No MFA, no SAML' },
  { key: 'complianceScore', label: 'Compliance score', placeholder: '0' },
  { key: 'usage', label: 'Usage', placeholder: '55 users | 57 IPs' },
  { key: 'traffic', label: 'Traffic', placeholder: '114 MB' },
];

export function ServiceDeliveryForm({ onChange }: Props) {
  const [solutionsUpdated, setSolutionsUpdated] = useState('');
  const [solutionsInstalled, setSolutionsInstalled] = useState('');
  const [analyticsUpdated, setAnalyticsUpdated] = useState('');
  const [analyticsEnabled, setAnalyticsEnabled] = useState('');
  const [huntCount, setHuntCount] = useState('');
  const [huntSummary, setHuntSummary] = useState('');
  const [huntAnalytics, setHuntAnalytics] = useState('');
  const [apps, setApps] = useState<ServiceDeliveryCloudApp[]>([]);
  const [otherWork, setOtherWork] = useState('');

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const su = splitLines(solutionsUpdated);
    const si = splitLines(solutionsInstalled);
    const au = splitLines(analyticsUpdated);
    const ae = splitLines(analyticsEnabled);
    const ra = splitLines(huntAnalytics);
    const cleanApps = apps.filter((a) => a.name.trim());
    const other = otherWork.trim();
    const countNum = huntCount.trim() ? Number(huntCount) : undefined;
    const count = countNum !== undefined && !Number.isNaN(countNum) ? countNum : undefined;
    const hasHunts = count !== undefined || huntSummary.trim().length > 0 || ra.length > 0;

    if (!su.length && !si.length && !au.length && !ae.length && !hasHunts && !cleanApps.length && !other) {
      onChangeRef.current(undefined);
      return;
    }
    onChangeRef.current({
      solutionsUpdated: su,
      solutionsInstalled: si,
      analyticsUpdated: au,
      analyticsEnabled: ae,
      hunts: { count, summary: huntSummary.trim() || undefined, resultingAnalytics: ra },
      cloudAppDiscovery: cleanApps,
      otherWork: other || undefined,
    });
  }, [solutionsUpdated, solutionsInstalled, analyticsUpdated, analyticsEnabled, huntCount, huntSummary, huntAnalytics, apps, otherWork]);

  function patchApp(i: number, patch: Partial<ServiceDeliveryCloudApp>) {
    setApps((p) => p.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-text-primary">Service delivery — what we did this month</h3>
        <p className="text-xs text-text-secondary mt-0.5">
          Your own write-up of the work delivered. Rendered into the report as-is (the AI never edits it). Leave blank to omit the section.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ListField label="Solutions updated" hint="Microsoft Entra ID Threat Intelligence (NEW)" value={solutionsUpdated} onChange={setSolutionsUpdated} />
        <ListField label="Solutions installed" hint="Web Shells Threat Protection" value={solutionsInstalled} onChange={setSolutionsInstalled} />
        <ListField label="Analytics updated" hint="Correlate Unfamiliar sign-in properties & atypical travel" value={analyticsUpdated} onChange={setAnalyticsUpdated} />
        <ListField label="Analytics enabled" hint="TI Map IP Entity to SigninLogs" value={analyticsEnabled} onChange={setAnalyticsEnabled} />
      </div>

      {/* Hunts */}
      <div className="border border-border rounded-lg p-3 space-y-2">
        <p className="text-xs font-medium text-text-secondary">Hunts completed</p>
        <div className="flex gap-3">
          <div className="w-24">
            <label className="block text-xs text-text-secondary mb-1">Count</label>
            <input type="number" min={0} value={huntCount} onChange={(e) => setHuntCount(e.target.value)} placeholder="4"
              className="w-full px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-sm" />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-text-secondary mb-1">Summary</label>
            <input type="text" value={huntSummary} onChange={(e) => setHuntSummary(e.target.value)}
              placeholder="4 hunts completed, resulting in 2 new custom analytics"
              className="w-full px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-sm" />
          </div>
        </div>
        <ListField label="Resulting analytics" hint="Spam and Phish allowed to inbox by User Overrides" value={huntAnalytics} onChange={setHuntAnalytics} />
      </div>

      {/* Cloud app discovery */}
      <div className="border border-border rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-text-secondary">Cloud app discovery (for review)</p>
          <button type="button" onClick={() => setApps((p) => [...p, { name: '' }])}
            className="text-brand-blue hover:opacity-80 inline-flex items-center gap-1 text-xs">
            <Plus className="w-3.5 h-3.5" /> Add app
          </button>
        </div>
        {apps.length === 0 && <p className="text-xs text-text-secondary">No apps added.</p>}
        {apps.map((app, i) => (
          <div key={i} className="border border-border rounded-lg p-2 bg-bg-secondary space-y-2">
            <div className="flex items-center gap-2">
              <input type="text" value={app.name} onChange={(e) => patchApp(i, { name: e.target.value })} placeholder="App name (e.g. Stores4Domains)"
                className="flex-1 px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-sm font-medium" />
              <button type="button" onClick={() => setApps((p) => p.filter((_, j) => j !== i))}
                className="text-text-secondary hover:text-red-600 p-1" aria-label="Remove app">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {APP_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-[11px] text-text-secondary mb-0.5">{f.label}</label>
                  <input type="text" value={app[f.key] ?? ''} onChange={(e) => patchApp(i, { [f.key]: e.target.value })} placeholder={f.placeholder}
                    className="w-full px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-xs" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Other work — free-form catch-all */}
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">
          Other work performed <span className="font-normal">(optional)</span>
        </label>
        <MarkdownField
          value={otherWork}
          onChange={setOtherWork}
          rows={5}
          placeholder={'Anything the categories above don’t cover — e.g. "Added the FortiGate firewall into monitoring" or "Created a watchlist for finance-team sign-ins". Bold, lists and headings supported.'}
        />
        <p className="text-[11px] text-text-secondary mt-1">
          Rendered at the bottom of the service-delivery section. You can also edit this later from the report preview.
        </p>
      </div>
    </div>
  );
}
